import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, RefreshCw, Inbox, ChevronDown, ChevronUp, Trash2, Clock } from 'lucide-react';
import { apiFetch } from '../lib/api';

interface Message {
  id: string; mail_from: string; subject: string | null;
  body_text: string | null; body_html: string | null; received_at: number;
}

export default function InboxDetail() {
  const { address } = useParams<{ address: string }>();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [htmlView, setHtmlView] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const res = await apiFetch(`/api/admin/inboxes/${encodeURIComponent(address!)}/messages`);
    if (res.ok) { const d = await res.json(); setMessages(d.messages || []); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [address]); // eslint-disable-line

  const toggle = (id: string) => setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleHtml = (id: string) => setHtmlView(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const expireInbox = async () => {
    if (!confirm(`Expire inbox "${address}"?`)) return;
    await apiFetch(`/api/admin/inboxes/${encodeURIComponent(address!)}/expire`, { method: 'POST' });
    navigate('/inboxes');
  };
  const deleteInbox = async () => {
    if (!confirm(`Delete inbox "${address}" and all ${messages.length} messages?`)) return;
    await apiFetch(`/api/admin/inboxes/${encodeURIComponent(address!)}`, { method: 'DELETE' });
    navigate('/inboxes');
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/inboxes')}
            className="p-2 rounded-xl bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition">
            <ArrowLeft size={16} />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 font-mono break-all">{address}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{messages.length} message{messages.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button onClick={expireInbox}
            className="flex items-center gap-2 px-3 py-2 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-xl text-sm font-medium hover:bg-amber-200 transition">
            <Clock size={14} /> Expire
          </button>
          <button onClick={deleteInbox}
            className="flex items-center gap-2 px-3 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-xl text-sm font-medium hover:bg-red-200 transition">
            <Trash2 size={14} /> Delete Inbox
          </button>
        </div>
      </div>

      {/* Messages */}
      {loading ? (
        <div className="py-16 text-center text-gray-400">
          <RefreshCw size={24} className="animate-spin mx-auto mb-3" />
          <p>Loading messages…</p>
        </div>
      ) : messages.length === 0 ? (
        <div className="py-16 text-center text-gray-400 bg-white/80 dark:bg-gray-800/80 rounded-2xl border border-gray-200/60 dark:border-gray-700/60">
          <Inbox size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium">No messages in this inbox.</p>
          <p className="text-sm mt-1 opacity-70">Messages will appear here when emails arrive.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {messages.map((msg) => {
            const isOpen = expanded.has(msg.id);
            const showHtml = htmlView.has(msg.id);
            return (
              <div key={msg.id}
                className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow border border-gray-200/60 dark:border-gray-700/60 overflow-hidden">
                {/* Message header — click to expand */}
                <button
                  onClick={() => toggle(msg.id)}
                  className="w-full flex items-center gap-4 p-4 hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shrink-0">
                    <Mail size={16} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {msg.subject || '(no subject)'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                          From: <span className="font-mono">{msg.mail_from || '—'}</span>
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-gray-400 whitespace-nowrap">
                          {new Date(msg.received_at * 1000).toLocaleString()}
                        </span>
                        {isOpen ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                      </div>
                    </div>
                  </div>
                </button>

                {/* Expanded body */}
                {isOpen && (
                  <div className="border-t border-gray-100 dark:border-gray-700">
                    {/* Toggle text/html */}
                    {msg.body_html && (
                      <div className="flex gap-2 px-4 pt-3">
                        <button onClick={() => toggleHtml(msg.id)}
                          className={`px-3 py-1 rounded-lg text-xs font-medium transition ${!showHtml ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`}>
                          Plain Text
                        </button>
                        <button onClick={() => toggleHtml(msg.id)}
                          className={`px-3 py-1 rounded-lg text-xs font-medium transition ${showHtml ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`}>
                          HTML Preview
                        </button>
                      </div>
                    )}

                    <div className="p-4">
                      {showHtml && msg.body_html ? (
                        <iframe
                          srcDoc={msg.body_html}
                          className="w-full rounded-xl border border-gray-200 dark:border-gray-700 min-h-[300px] bg-white"
                          sandbox="allow-same-origin"
                          title="Email HTML preview"
                        />
                      ) : (
                        <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-mono bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 max-h-[400px] overflow-y-auto leading-relaxed">
                          {msg.body_text || '(no text body)'}
                        </pre>
                      )}
                    </div>

                    {/* Message metadata */}
                    <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-3 flex flex-wrap items-center gap-4 text-xs text-gray-400">
                      <span>ID: <code className="font-mono">{msg.id}</code></span>
                      <span>Received: {new Date(msg.received_at * 1000).toISOString()}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
